import { useEffect, useState } from 'react'

/**
 * Company logo, falling back to initials.
 *
 * Many logo URLs in the imported data point at Google's favicon service or at a
 * company CDN, and a fair number will eventually 404. A broken-image icon next
 * to every third company reads as a broken CRM, so a failed load degrades to
 * the company's initials instead.
 */
export function LeadLogo({
  name,
  src,
  size = 52,
  radius,
}: {
  name: string
  src: string
  size?: number
  /** Corner radius in px. Defaults to the token radius for the 52px avatar. */
  radius?: number
}) {
  const [failed, setFailed] = useState(false)

  // A new lead may reuse this component instance; reset on src change.
  useEffect(() => setFailed(false), [src])

  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('')

  if (!src || failed) {
    return (
      <div
        className="leadlogo leadlogo--fallback"
        style={{ width: size, height: size, fontSize: size * 0.34, borderRadius: radius }}
        aria-hidden="true"
      >
        {initials || '?'}
      </div>
    )
  }

  return (
    <img
      className="leadlogo"
      src={src}
      alt=""
      width={size}
      height={size}
      loading="lazy"
      onError={() => setFailed(true)}
      style={{ width: size, height: size, borderRadius: radius }}
    />
  )
}
